import { Button } from "@/components/ui/baseUi/button"
import { DateInput, TimeField } from "@/components/ui/baseUi/datefield-rac"
import { AssignedCoachInput } from "@/components/ui/forms/assignedCoachInput"
import { DropdownInput } from "@/components/ui/forms/dropdownInput"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { Label } from "@/components/ui/baseUi/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { BoxProgramTemplateCalendarEvent } from "@/modules/boxProgrammation/components/boxProgramTemplateCalendarEvent"
import { useBoxProgram } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { useBoxProgramSlotDefaultSettings } from "@/modules/boxProgrammation/hooks/useBoxProgramSlotDefaultSettings"
import { BOX_SLOT_PARTICIPANTS_LIMIT_MAX, BOX_SLOT_PARTICIPANTS_LIMIT_MIN } from "@/modules/boxSlot/boxSlotTypes"
import { useDateTimeCalendarProps } from "@/modules/boxSlot/hooks/useDateTimeCalendarProps"
import { BoxSlotTemplateEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateEntity"
import { useBoxSlotTemplateActions } from "@/modules/boxSlotTemplate/store/useBoxSlotTemplateStore"
import { cn } from "@/modules/lib/utils"
import { DropInIcon, DropInSelectedIcon } from "@images/svgIcons/slots/drop-in"
import { MultiSlotsIcon, MultiSlotsSelectedIcon } from "@images/svgIcons/slots/multi-slots"
import { TrialSessionIcon, TrialSessionSelectedIcon } from "@images/svgIcons/slots/trial-session"
import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import { Time } from "@internationalized/date"
import {
  getCancellationBeginningOptions,
  getRegistrationBeginningOptions,
  getRegistrationEndOptions,
} from "@modules/boxSlotTemplate/boxSlotTemplateUtils"
import {
  BoxSlotTemplateFormDeleteModal,
  type BoxSlotTemplateFormDeleteModalRef,
} from "@modules/boxSlotTemplate/components/boxSlotTemplateFormDeleteModal"
import {
  BoxSlotTemplateFormModal,
  type BoxSlotTemplateFormModalRef,
} from "@modules/boxSlotTemplate/components/boxSlotTemplateFormModal"
import { useBoxSlotTemplateFormik } from "@modules/boxSlotTemplate/hooks/useBoxSlotTemplateFormik"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { HUTimeDropdownOrCustomizedTimeInput } from "@modules/ui/components/huDropdownWithCustomizedTimeInput"
import { useLocaleWeekDays } from "@modules/utils/hooks/useWeekdays"
import dayjs from "dayjs"
import type { FieldInputProps } from "formik"
import { ClockIcon, LoaderCircle } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"

export function BoxSlotTemplateForm({
  boxProgramId,
  boxSlotTemplate,
  dateToCreateSlotTemplateOn,
  className,
  ...props
}: {
  boxProgramId: string
  boxSlotTemplate: BoxSlotTemplateEntity | null
  dateToCreateSlotTemplateOn?: Date
} & React.ComponentProps<"div">) {
  const isEdition = !!boxSlotTemplate
  const { hideSidebar } = useSidebar()

  const { data: defaultSettings } = useBoxProgramSlotDefaultSettings(boxProgramId)
  const { data: boxProgram } = useBoxProgram(boxProgramId)
  const { addBoxSlotTemplateBeingCreated, updateBoxSlotTemplateBeingCreated, clearBoxSlotTemplateBeingCreated } =
    useBoxSlotTemplateActions()

  const boxSlotTemplateFormik = useMemo(
    () =>
      boxSlotTemplate ??
      BoxSlotTemplateEntity.fromDefaultSettings(defaultSettings, dayjs(dateToCreateSlotTemplateOn), boxProgram),
    [boxSlotTemplate, defaultSettings, dateToCreateSlotTemplateOn, boxProgram],
  )

  const weekDaysOptions = useLocaleWeekDays()
  const formik = useBoxSlotTemplateFormik(boxProgramId, boxSlotTemplateFormik, isEdition)
  const { validateStartDateTime } = useDateTimeCalendarProps()

  const registrationBeginningOptions = getRegistrationBeginningOptions()
  const registrationEndOptions = getRegistrationEndOptions()
  const cancellationBeginningOptions = getCancellationBeginningOptions()
  const boxSlotTemplateFormModal = useRef<BoxSlotTemplateFormModalRef>(null)
  const boxSlotTemplateFormDeleteModal = useRef<BoxSlotTemplateFormDeleteModalRef>(null)
  const [formikInitialValues] = useState(formik.initialValues)
  const containerRef = useRef<HTMLDivElement>(null)

  const confirmDeleteBoxSlotTemplate = () => {
    boxSlotTemplateFormDeleteModal.current?.open()
  }

  const toggleMultiBooking = () => {
    void formik.setFieldValue("allowMultiBooking", !formik.values.allowMultiBooking)
  }

  const toggleDropIn = () => {
    void formik.setFieldValue("dropinsAccepted", !formik.values.dropinsAccepted)
  }

  const toggleTrialSessions = () => {
    void formik.setFieldValue("trialsAccepted", !formik.values.trialsAccepted)
  }

  const submitForm = async () => {
    const changedKeys = boxSlotTemplateFormModal.current?.changedKeys() ?? []

    if (isEdition) {
      if (changedKeys.length > 0) boxSlotTemplateFormModal.current?.open()
      else handleHideSidebar()
    } else await handleSubmit()
  }

  const handleHideSidebar = () => {
    hideSidebar()
    clearBoxSlotTemplateBeingCreated()
  }

  const handleSubmit = async () => {
    const errorElements = containerRef.current?.querySelectorAll('[aria-invalid="true"]')
    if (errorElements && errorElements.length > 0) {
      document
        .querySelector('[data-slot="right-sidebar-content"]')
        ?.scrollTo({ top: (errorElements[0] as HTMLElement).offsetTop - 30, behavior: "smooth" })
    }
    await formik.submitForm()
  }

  useEffect(() => {
    void addBoxSlotTemplateBeingCreated([boxSlotTemplateFormik])
  }, [addBoxSlotTemplateBeingCreated, boxSlotTemplateFormik])

  useEffect(() => {
    void updateBoxSlotTemplateBeingCreated([BoxSlotTemplateEntity.fromFormikEntity(formik.values, boxProgram)])
  }, [formik.values, boxProgram, updateBoxSlotTemplateBeingCreated])

  return (
    <>
      <div className={cn("flex flex-col gap-6 pb-4", className)} {...props} ref={containerRef}>
        <div className="flex justify-between pb-6">
          <BoxProgramTemplateCalendarEvent
            event={boxSlotTemplateFormik.toCalendarEventTypeWithHour()}
            key={boxSlotTemplate?.id ?? ""}
            canBeClicked={false}
            isOverlayed={true}
            relativePosition
          />
          <HUCardAlert
            title={word("programmation.slotTemplates.form.alert.label")}
            icon={<WarningHexagonalIcon width={55} height={55} color="currentColor" className="text-white" />}
            className="w-8/12 items-center rounded-2xl py-3"
            titleFontStyle="BM"
          />
        </div>

        <DropdownInput
          label={word("programmation.slotTemplates.form.weekday.label")}
          isRequiredInput
          error={getFormErrorMessage("weekDay", formik)}
          options={weekDaysOptions}
        >
          <Select
            defaultValue={formik.values.weekDay}
            onValueChange={(value) => {
              void formik.setFieldValue("weekDay", value)
            }}
            items={weekDaysOptions}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {weekDaysOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DropdownInput>

        <div className="flex gap-2">
          <TimeField
            className="w-full *:not-first:mt-2"
            aria-label={word("programmation.slotTemplates.form.hour.startTime")}
            shouldForceLeadingZeros={true}
            value={new Time(formik.values.startDateTime.getHours(), formik.values.startDateTime.getMinutes())}
            onChange={(e) => {
              if (e?.hour !== undefined && e?.minute !== undefined) {
                const newStartDateTime = dayjs()
                  .set("hour", e.hour)
                  .set("minute", e.minute)
                  .set("second", 0)
                  .set("millisecond", 0)
                  .toDate()

                validateStartDateTime(newStartDateTime, formik.values.endDateTime, (val) => {
                  void formik.setFieldValue("endDateTime", val)
                })

                void formik.setFieldValue("startDateTime", newStartDateTime)
              }
            }}
            onBlur={() => {
              void formik.setFieldTouched("startDateTime", true)
            }}
          >
            <Label className="text-foreground text-xs font-medium uppercase">
              {word("programmation.slotTemplates.form.hour.startTime")}

              <Text fontStyle="LL" className="text-red-500">
                {" "}
                *
              </Text>
            </Label>
            <div className="relative">
              <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 inset-s-0 z-10 flex items-center justify-center ps-3">
                <ClockIcon size={16} aria-hidden="true" />
              </div>
              <DateInput className="ps-9" />
            </div>
            {getFormErrorMessage("startDateTime", formik)}
          </TimeField>

          <TimeField
            className="w-full *:not-first:mt-2"
            aria-label={word("programmation.slotTemplates.form.hour.endTime")}
            value={new Time(formik.values.endDateTime.getHours(), formik.values.endDateTime.getMinutes())}
            shouldForceLeadingZeros={true}
            onChange={(e) => {
              if (e?.hour !== undefined && e?.minute !== undefined) {
                const newEndDateTime = dayjs()
                  .set("hour", e.hour)
                  .set("minute", e.minute)
                  .set("second", 0)
                  .set("millisecond", 0)
                  .toDate()

                void formik.setFieldValue("endDateTime", newEndDateTime)
              }
            }}
            onBlur={() => {
              void formik.setFieldTouched("endDateTime", true)
            }}
          >
            <Label className="text-foreground text-xs font-medium uppercase">
              {word("programmation.slotTemplates.form.hour.endTime")}

              <Text fontStyle="LL" className="text-red-500">
                {" "}
                *
              </Text>
            </Label>
            <div className="relative">
              <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 start-0 z-10 flex items-center justify-center ps-3">
                <ClockIcon size={16} aria-hidden="true" />
              </div>
              <DateInput className="ps-9" />
            </div>
          </TimeField>
        </div>

        <StandardInput
          isRequiredInput
          label={word("programmation.slotTemplates.form.participantLimit.label")}
          getFieldProps={formik.getFieldProps}
          name="participantLimit"
          error={getFormErrorMessage("participantLimit", formik, { errorMessage: word("global.form.required") })}
          setFieldValue={formik.setFieldValue}
          inputProps={{
            type: "number",
            inputMode: "numeric",
            min: BOX_SLOT_PARTICIPANTS_LIMIT_MIN,
            max: BOX_SLOT_PARTICIPANTS_LIMIT_MAX,
            step: 1,
            onChange: (e) => {
              const value = parseInt(e.target.value)
              if (value > BOX_SLOT_PARTICIPANTS_LIMIT_MAX) {
                void formik.setFieldValue("participantLimit", BOX_SLOT_PARTICIPANTS_LIMIT_MAX)
              } else if (value < BOX_SLOT_PARTICIPANTS_LIMIT_MIN) {
                void formik.setFieldValue("participantLimit", BOX_SLOT_PARTICIPANTS_LIMIT_MIN)
              } else {
                void formik.setFieldValue("participantLimit", value)
              }
            },
            onFocus: (e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false }),
          }}
        />

        <div className="flex flex-row items-center justify-between gap-2">
          {/* Drop in accepted */}
          <div
            className={cn(
              "flex w-full cursor-pointer flex-col items-center justify-between gap-2 self-stretch rounded-2xl border-solid px-2 py-3 transition-all duration-100",
              formik.values.dropinsAccepted
                ? "border border-primary-500"
                : "border border-borderDefault hover:border-borderDefault-hover",
            )}
            onClick={toggleDropIn}
          >
            {formik.values.dropinsAccepted ? <DropInSelectedIcon color="" /> : <DropInIcon color="" />}
            <Text
              fontStyle="TS"
              className={`text-center ${formik.values.dropinsAccepted ? "text-textDefault-body" : "text-textDisabled"}`}
            >
              {formik.values.dropinsAccepted
                ? word("box.slotTemplateGroup.form.dropIn.allowed")
                : word("box.slotTemplateGroup.form.dropIn.rejected")}
            </Text>
          </div>

          {/* Trial session accepted*/}
          <div
            className={cn(
              "flex w-full cursor-pointer flex-col items-center justify-between gap-2 self-stretch rounded-2xl border-solid px-2 py-3 transition-all duration-100",
              formik.values.trialsAccepted
                ? "border border-primary-500"
                : "border border-borderDefault hover:border-borderDefault-hover",
            )}
            onClick={toggleTrialSessions}
          >
            {formik.values.trialsAccepted ? <TrialSessionSelectedIcon color="" /> : <TrialSessionIcon color="" />}
            <Text
              fontStyle="TS"
              className={`text-center ${formik.values.trialsAccepted ? "text-textDefault-body" : "text-textDisabled"}`}
            >
              {formik.values.trialsAccepted
                ? word("box.slotTemplateGroup.form.trialSession.allowed")
                : word("box.slotTemplateGroup.form.trialSession.rejected")}
            </Text>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Text fontStyle="LS" className="uppercase">
            {word("box.slotTemplateGroup.form.registration.label")}
          </Text>
          <Text fontStyle="BS">{word("box.slotTemplateGroup.form.registration.description")}</Text>
          <div className="flex gap-2">
            <HUTimeDropdownOrCustomizedTimeInput
              name="limitStart"
              dropdownOptions={registrationBeginningOptions}
              getFieldProps={() => formik.getFieldProps("limitStart") as FieldInputProps<number>}
              label={word("box.slotTemplateGroup.form.registration.beginning")}
              setFieldValue={formik.setFieldValue}
              error={getFormErrorMessage(`limitStart`, formik)}
            />
            <HUTimeDropdownOrCustomizedTimeInput
              name="limitEnd"
              dropdownOptions={registrationEndOptions}
              label={word("box.slotTemplateGroup.form.registration.end")}
              getFieldProps={() => formik.getFieldProps("limitEnd") as FieldInputProps<number>}
              setFieldValue={formik.setFieldValue}
              error={getFormErrorMessage(`limitEnd`, formik)}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Text fontStyle="LS" className="uppercase">
            {word("box.slotTemplateGroup.form.cancellation.label")}
            <Text fontStyle="LL" className="text-red-500">
              {" *"}
            </Text>
          </Text>
          <Text fontStyle="BS">{word("box.slotTemplateGroup.form.cancellation.description")}</Text>
          <HUTimeDropdownOrCustomizedTimeInput
            name="limitCancel"
            dropdownOptions={cancellationBeginningOptions}
            label={word("box.slotTemplateGroup.form.cancellation.beginning")}
            getFieldProps={() => formik.getFieldProps("limitCancel") as FieldInputProps<number>}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage(`limitCancel`, formik)}
          />
        </div>

        <div
          className={cn(
            "flex w-full cursor-pointer flex-row gap-4 rounded-3xl border-solid p-6 transition-all duration-100",
            formik.values.allowMultiBooking
              ? "border border-primary-500"
              : "border border-borderDefault hover:border-borderDefault-hover",
          )}
          onClick={toggleMultiBooking}
        >
          <div className="flex w-[45%] flex-col items-center gap-3">
            {formik.values.allowMultiBooking ? <MultiSlotsSelectedIcon color="" /> : <MultiSlotsIcon color="" />}
            <Text
              fontStyle="TS"
              className={`text-center ${formik.values.allowMultiBooking ? "text-textDefault-body" : "text-textDisabled"}`}
            >
              {formik.values.allowMultiBooking
                ? word("box.slotTemplateGroup.form.mutliSlotBooking.allowed")
                : word("box.slotTemplateGroup.form.mutliSlotBooking.rejected")}
            </Text>
          </div>
          <Text fontStyle="BS" className="pre-line flex w-[55%] text-textDefault-caption">
            {word("box.slotTemplateGroup.form.mutliSlotBooking.description")}
          </Text>
        </div>

        <StandardInput
          label={word("box.slotTemplateGroup.form.area.title")}
          description={word("box.slotTemplateGroup.form.area.subtitle")}
          name="location"
          isRequiredInput={true}
          getFieldProps={formik.getFieldProps}
          setFieldValue={formik.setFieldValue}
          error={getFormErrorMessage("location", formik)}
          inputProps={{
            placeholder: word("box.slotTemplateGroup.form.area.placeholder"),
          }}
        />

        <div className="flex flex-col">
          <AssignedCoachInput
            label={word("box.slotTemplateGroup.form.assignedStaffMembers.label")}
            textDescription={word("box.slotTemplateGroup.form.assignedStaffMembers.description")}
            name="assignedStaffMembers"
            isRequiredInput={false}
            setFieldValue={formik.setFieldValue}
            getFieldProps={formik.getFieldProps}
            error={getFormErrorMessage("assignedStaffMembers", formik)}
          />
        </div>

        <div className="mt-3 flex justify-end">
          <div className="flex w-full flex-row gap-3">
            {isEdition ? (
              <Button variant="outline" size="lg" onClick={() => confirmDeleteBoxSlotTemplate()} className="flex-1">
                {word("global.delete")}
              </Button>
            ) : (
              <Button variant="outline" size="lg" onClick={() => handleHideSidebar()} className="flex-1">
                {word("global.cancel")}
              </Button>
            )}
            <Button
              variant="primary"
              size="lg"
              onClick={() => void submitForm()}
              className="mr-2 flex-1"
              disabled={formik.isSubmitting}
              isLoading={formik.isSubmitting}
            >
              {word("box.slot.form.submit.button")}
              {formik.isSubmitting && <LoaderCircle className="animate-spin" />}
            </Button>
          </div>
        </div>
      </div>
      <BoxSlotTemplateFormModal
        formik={formik}
        formikInitialValues={formikInitialValues}
        handleSubmit={handleSubmit}
        ref={boxSlotTemplateFormModal}
      />
      {boxSlotTemplate && (
        <BoxSlotTemplateFormDeleteModal
          boxProgram={boxProgram}
          boxSlotTemplate={boxSlotTemplate}
          ref={boxSlotTemplateFormDeleteModal}
        />
      )}
    </>
  )
}
